import React, { useState, useCallback, useMemo } from 'react';

import { ProjectDetailPageProps, EEGTariffUpdate, MarktpreisTariffUpdate, ValidationsResponse } from '../../../types';
import { useTariffsData } from './hooks/useTariffsData';
import { useComputedRevenues } from './hooks/useComputedRevenues';
import { useToast } from '../../../contexts/ToastContext';
import { usePageWarnings } from '../../../contexts/PageWarningsContext';
import Section from '../../layout/Section';
import PageTemplate from '../../layout/PageTemplate';
import EEGTariffSection from './sections/EEGTariffSection';
import MarketPriceSection from './sections/MarketPriceSection';
import PPATariffSection from './sections/PPATariffSection';
import RevenuesPerYearSection from './sections/RevenuesPerYearSection';

/** Wandelt einen formatierten String (deutsches Format) in eine Zahl um */
const strToNum = (value: string): number | undefined => {
    if (!value || value.trim() === '') return undefined;
    const cleanValue = value.replace(/[^\d,.-]/g, '').replace(',', '.');
    const num = parseFloat(cleanValue);
    return isNaN(num) ? undefined : num;
};

interface TariffsPageProps extends ProjectDetailPageProps {
    canView: boolean;
    canEdit: boolean;
}

/**
 * Tariffs Page Component
 * Detailansicht der Tarife eines Windpark-Projekts
 * Daten werden aus der API geladen und sind bei entsprechender Berechtigung bearbeitbar
 * 
 * Erlöse pro Jahr werden im Frontend berechnet (Client-Side Derived State),
 * sodass das Chart immer konsistent mit den angezeigten Tarifen ist.
 */
const TariffsPage: React.FC<TariffsPageProps> = ({
    project,
    canEdit,
}) => {
    const { showSuccess, showError, showInfo } = useToast();

    // Verwende PageWarningsContext für API-basierte Validierungen
    // Der Context ist die Single Source of Truth für Validierungen
    const { setValidationsFromAPI, hasWarning, getValidationEntries } = usePageWarnings();

    // Hole Validierungseinträge aus dem Context (nicht aus useTariffsData)
    // Diese werden sowohl bei Projektlaufzeit-Änderungen als auch bei PPA-CRUD aktualisiert
    const validationEntries = getValidationEntries('project-revenues');

    const handleValidationsReceived = useCallback((validations: ValidationsResponse) => {
        // Validierungen an den Context weitergeben (setzt Warning-Status automatisch)
        setValidationsFromAPI(validations);
    }, [setValidationsFromAPI]);

    // Warning-Status für PPA-Section
    const ppaHasWarning = hasWarning('project-revenues');

    const {
        eegTariff,
        marktpreisTariff,
        ppaTariffs,
        activeYieldForecast,
        isLoading,
        error,
        isSaving,
        updateEEGField,
        updateMarktpreisField,
        createPPATariff,
        updatePPATariff,
        deletePPATariff,
        refreshBackendRevenues,
    } = useTariffsData({
        projectId: project.id,
        onValidationsReceived: handleValidationsReceived,
    });

    // Erlöse im Frontend berechnen (Client-Side Derived State)
    // Reagiert automatisch auf Änderungen an Tarifen, PPAs und Ertragsprognose
    const computedRevenues = useComputedRevenues({
        eegTariff,
        ppaTariffs,
        marktpreisTariff,
        activeYieldForecast,
        aktiverErtragswert: project.aktiver_ertragswert,
        laufzeitJahre: project.laufzeit_jahre || 20,
        projectId: project.id,
    });

    // State für den Aktualisieren-Button
    const [isRefreshing, setIsRefreshing] = useState(false);

    /**
     * Handler für den Aktualisieren-Button.
     * Holt die Backend-berechneten Erlöse via GET und zeigt Toast-Feedback.
     */
    const handleRefreshRevenues = useCallback(async () => {
        setIsRefreshing(true);
        try {
            const backendRevenues = await refreshBackendRevenues();
            if (backendRevenues) {
                showSuccess('Erlöse aktualisiert');
            } else {
                showInfo('Keine Backend-Erlöse vorhanden. Das Backend hat noch keine Erlöse berechnet.');
            }
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Fehler beim Laden der Backend-Erlöse';
            showError(errorMsg);
        } finally {
            setIsRefreshing(false);
        }
    }, [refreshBackendRevenues, showSuccess, showInfo, showError]);

    // Warning-Status für EEG-Section: Voraussetzungen für Berechnung prüfen
    const { eegHasWarning, eegWarningTooltip } = useMemo(() => {
        if (!eegTariff) return { eegHasWarning: false, eegWarningTooltip: '' };

        const keineAnlagen = eegTariff.referenzertrag == null;
        const keinePrognose = eegTariff.standortguete == null && !keineAnlagen;

        if (keineAnlagen && !activeYieldForecast) {
            return {
                eegHasWarning: true,
                eegWarningTooltip: 'Keine Anlagen und keine aktive Ertragsprognose vorhanden. Standortgüte, Korrekturfaktor und Preis/kWh können nicht berechnet werden.',
            };
        }
        if (keineAnlagen) {
            return {
                eegHasWarning: true,
                eegWarningTooltip: 'Keine Anlagen mit Referenzertrag vorhanden. Standortgüte, Korrekturfaktor und Preis/kWh können nicht berechnet werden.',
            };
        }
        if (keinePrognose) {
            return {
                eegHasWarning: true,
                eegWarningTooltip: 'Keine aktive Ertragsprognose vorhanden. Standortgüte und Korrekturfaktor können nicht berechnet werden.',
            };
        }
        return { eegHasWarning: false, eegWarningTooltip: '' };
    }, [eegTariff, activeYieldForecast]);

    const handleEEGChange = useCallback(
        (field: keyof EEGTariffUpdate) => (value: string) => {
            updateEEGField(field, strToNum(value));
        },
        [updateEEGField]
    );

    const handleMarktpreisChange = useCallback(
        (field: keyof MarktpreisTariffUpdate) => (value: string) => {
            updateMarktpreisField(field, strToNum(value));
        },
        [updateMarktpreisField]
    );

    return (
        <PageTemplate isLoading={isLoading} isSaving={isSaving} saveError={error}>
            <Section title="EEG-Tarif" hasWarning={eegHasWarning} warningTooltip={eegWarningTooltip}>
                <EEGTariffSection
                    eegTariff={eegTariff}
                    canEdit={canEdit}
                    onFieldChange={handleEEGChange}
                />
            </Section>

            <PPATariffSection
                ppaTariffs={ppaTariffs}
                canEdit={canEdit}
                projektLaufzeit={project.laufzeit_jahre!}
                onCreatePPA={createPPATariff}
                onUpdatePPA={updatePPATariff}
                onDeletePPA={deletePPATariff}
                validationEntries={validationEntries}
                hasWarning={ppaHasWarning}
            />

            <MarketPriceSection
                marktpreisTariff={marktpreisTariff}
                canEdit={canEdit}
                onMarktpreisChange={handleMarktpreisChange}
            />

            <RevenuesPerYearSection
                energyRevenues={computedRevenues}
                onRefresh={handleRefreshRevenues}
                isRefreshing={isRefreshing}
            />
        </PageTemplate>
    );
};

export default TariffsPage;
